// Quotation documents and statuses (Stage 2E-2): status names, the issued copy (generated from the frozen snapshots and the
// database's lines/totals only, stored once with its SHA-256), the verified download, the CANCELLED-marked copy and the
// draft preview. Never calculates money: every figure comes from the database.
import { quotationPdfBytes } from "./quotationPdf.js";
import { loadQuotation, loadQuotationLines, loadPdfImage, loadStampImage, storeQuotationPdf, openStoredPdf } from "./api";
import { pdfFileName, unprintable } from "./format.js";

// Display status: 014's open state 'sent' is shown as Issued until it is marked sent (owner decision D1).
export const qStatus = (q) => (q.status === "sent" ? (q.sent_at ? "sent" : "issued") : q.status);
export const STATUS = {
    draft: ["Draft", "draft"], issued: ["Issued", "current"], sent: ["Sent", "upcoming"], accepted: ["Accepted", "confirmed"],
    declined: ["Declined", "off"], expired: ["Expired", "off"], converted: ["Converted", "posted"], cancelled: ["Cancelled", "overdue"],
};
export const OPEN = ["draft", "issued", "sent", "accepted"];
// D2: expiry is marked by the Admin; the app only warns once the valid-until date has passed.
export const validityPassed = (q, today) => q.status === "sent" && q.valid_until < today;

// Everything the PDF prints must be printable with its font (see format.js); checked before issuing.
export function unprintableFor({ settings, client, draft }) {
    return unprintable([
        ["Company name", settings?.company_name], ["Company address", settings?.address], ["Company phone", settings?.phone], ["Company email", settings?.email],
        ["Website", settings?.website], ["Bank", settings?.bank_name], ["Account name", settings?.bank_account_name], ["Branch", settings?.bank_branch],
        ["Client name", client?.name], ["Client contact person", client?.contact_person], ["Client address", client?.address], ["Client phone", client?.phone], ["Client email", client?.email],
        ["Subject", draft?.subject], ["Terms", draft?.terms], ["Notes", draft?.notes],
        ...(draft?.lines || []).flatMap((l, i) => [[`Line ${i + 1} description`, l.description], [`Line ${i + 1} unit`, l.unit]]),
    ]);
}

async function images(issuer) {
    const [logo, qr] = await Promise.all([loadPdfImage(issuer.logo_path), loadPdfImage(issuer.qr_path)]);
    return { logo, qr };
}

// The issued copy: re-read the quotation and its lines from the database and draw them from the snapshots.
export async function issuedPdfBytes(id, cal, mode = "issued") {
    const [q, lines] = await Promise.all([loadQuotation(id), loadQuotationLines(id)]);
    if (!q || q.status === "draft" || !q.issuer_snapshot) throw new Error("Only an issued quotation has an issued copy.");
    const [{ logo, qr }, stamp] = await Promise.all([images(q.issuer_snapshot), loadStampImage()]);
    return { q, bytes: quotationPdfBytes({ mode, quotation: q, lines, issuer: q.issuer_snapshot, client: q.client_snapshot, cal, logo, qr, stamp }) };
}

// "Create stored copy" — at issue, and later as the recovery when storing failed. Never replaces an existing copy.
export async function createStoredCopy(id, cal) {
    const { q, bytes } = await issuedPdfBytes(id, cal, "issued");
    if (q.issued_pdf_path) return q;
    return storeQuotationPdf(q, bytes);
}

export async function cancelledCopyBytes(id, cal) {
    return (await issuedPdfBytes(id, cal, "cancelled")).bytes;
}

// Draft preview from the current settings/client and the database's preview lines and totals.
export async function draftPdfBytes({ draft, preview, settings, client, cal }) {
    const { logo, qr } = await images(settings || {});
    const q = { ...draft, ...preview, doc_no: null, issued_at: null, updated_at: new Date().toISOString() };
    return quotationPdfBytes({ mode: "draft", quotation: q, lines: preview.lines, issuer: settings || {}, client: client || {}, cal, logo, qr });
}

export const storedCopyBytes = (q) => openStoredPdf(q);

// Show / save / print PDF bytes. `win` is a tab opened synchronously by the click (so pop-up blockers allow it).
export function deliver(bytes, { action, name, win }) {
    const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
    setTimeout(() => URL.revokeObjectURL(url), 120000);
    if (action === "download") {
        const a = document.createElement("a"); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
        win?.close();
    } else if (action === "print") {
        win?.close();
        const f = document.createElement("iframe");
        f.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
        f.src = url; document.body.appendChild(f);
        f.onload = () => { try { f.contentWindow.focus(); f.contentWindow.print(); } catch { window.open(url, "_blank"); } setTimeout(() => f.remove(), 60000); };
    } else if (win) win.location.href = url;
    else window.open(url, "_blank");
}
export const quotationFileName = (q, suffix) => pdfFileName(q.doc_no, suffix);
